import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  Chip,
  Divider,
  Link
} from '@material-ui/core';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import {
  AccessTime,
  CancelOutlined,
  CheckCircleOutlined,
  HourglassEmpty
} from '@material-ui/icons';
import RadioButtonCheckedIcon from '@material-ui/icons/RadioButtonChecked';
import { makeStyles } from '@material-ui/styles';
import ActionButton from 'components/ActionButton';
import {
  updatePageNo,
  updateSortBy,
  updateSortOrder,
  resetValues,
  updateRowsPerPage
} from 'store/helpers/tableAndToolbar/actions';
import listHelpers from 'store/helpers/tableAndToolbar/listHelpers';

const useStyles = makeStyles(theme => ({
  root: {},
  rootContent: {
    marginTop: theme.spacing(2)
  },
  content: {
    padding: 0
  },
  actions: {
    justifyContent: 'flex-end'
  },
  rightCol: {
    textAlign: 'right'
  },
  tableRow: {
    cursor: 'pointer'
  },
  noResultsDiv: {
    textAlign: 'center',
    padding: '16px',
    fontSize: '16px'
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    margin: '20%'
  },
  iconFont: {
    fontSize: 'medium',
    margin: '0px 4px 0px 0px',
    verticalAlign: 'sub'
  },
  warning: {
    fontSize: 'medium',
    margin: '0px 4px 0px 0px',
    verticalAlign: 'sub',
    color: 'red'
  },
  tooltip: { 
    cursor: 'pointer',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    maxWidth: '200px'
  }
}));

const TableComponent = props => {
  const {
    isLoading,
    tableTitles,
    onDoubleClickRow,
    actionButtonOptions,
    filteredRows,
    action,
    disableTableSort,
    onClickLink
  } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const { isTenantDefaultTemplate } = listHelpers();

  const rowsPerPage = useSelector(store => store.tableAndToolbar.rowsPerPage);
  const pageNo = useSelector(store => store.tableAndToolbar.pageNo);
  const totalCount = useSelector(store => store.tableAndToolbar.totalCount);
  const sortOrder = useSelector(store => store.tableAndToolbar.sortOrder);
  const sortBy = useSelector(store => store.tableAndToolbar.sortBy);
  const currentTenantId = useSelector(
    store => store.user.selectedTenant.tenantId
  );
  const currentBusinessContextId = useSelector(
    store => store.user.selectedBusinessContext.businessContextId
  );


  const handlePageChange = (event, page) => {
    dispatch(updatePageNo(page));
    dispatch(action);
  };

  const handleRowsPerPageChange = event => {
    dispatch(updateRowsPerPage(event.target.value));
    dispatch(updatePageNo(0));
    dispatch(action);
  };

  const onClickTableSort = tableTitleId => {
    let orderBy = sortOrder === 'ASC' ? 'DESC' : 'ASC';
    dispatch(updateSortOrder(tableTitleId === sortBy ? orderBy : 'ASC'));
    dispatch(updateSortBy(tableTitleId));
    dispatch(action);
  };

  const onDoubleClick = row => {
    if (onDoubleClickRow && typeof onDoubleClickRow === 'function') {
      onDoubleClickRow(row);
    }
  };

  const renderIcons = row => {
    switch (row.status) {
      case 'COMPLETED':
        return (
          <CheckCircleOutlined className={classes.iconFont} color="primary" />
        );
      case 'ABORTED':
        return <CancelOutlined className={classes.warning} />;
      case 'CREATED':
        return (
          <RadioButtonCheckedIcon
            className={classes.iconFont}
            color="secondary"
          />
        );
      case 'QUEUED':
        return <AccessTime className={classes.iconFont} color="secondary" />;
      case 'PROCESSING':
        return (
          <HourglassEmpty className={classes.iconFont} color="secondary" />
        );
    }
  };

  const renderGlobalDefaultTags = row => {
    return (
      row.defaultTemplate && (
        <Chip
          style={{ marginLeft: '12px' }}
          label="Global Default"
          color="primary"
          size="small"
        />
      )
    );
  };

  const renderTenantDefaultTags = row => {
    return (
      isTenantDefaultTemplate(row) && (
        <Chip
          style={{ marginLeft: '12px' }}
          label="Tenant Default"
          size="small"
        />
      )
    );
  };

  const renderUserTags = row => {
    return (
      row.tags?.length > 0 &&
      row.tags.map(tag => (
        <Tooltip title={tag}>
          <Chip
            style={{
              margin: '12px 0px 12px 12px',
              maxWidth: '100px'
            }}
            label={tag}
            variant="outlined"
            size="small"
          />
        </Tooltip>
      ))
    );
  };

  const renderTooltip = (tooltipContent, content) => {
    return (
      <Tooltip
        title={tooltipContent}
        placement="bottom">
        <div className={classes.tooltip}>{content}</div>
      </Tooltip>
    );
  };

  const [clipboardInfo, setClipboardInfo] = useState({
    msg: 'Copy to Clipboard'
  });

  const renderCopyIcon = row => {
    const handleTooltipToggle = () => {
      setClipboardInfo({ msg: 'Copy to Clipboard' });
    };

    const copyToClipboard = async text => {
      await navigator.clipboard.writeText(text);
      setClipboardInfo({ msg: 'Copied to Clipboard' });
    };

    return (
      <Tooltip
        title={clipboardInfo.msg}
        onClose={handleTooltipToggle}
        interactive={true}>
        <FileCopyOutlinedIcon
          style={{
            cursor: 'pointer',
            margin: '0px 0px 0px 12px',
            color: '#777'
          }}
          onMouseEnter={handleTooltipToggle}
          fontSize={'small'}
          onClick={() => copyToClipboard(row.id || row._id)}
        />
      </Tooltip>
    );
  };

  const renderCampaignToolTip = (row, tableTitle) => {
    if (row[tableTitle.id] === 'Automatic') {
      return (
        <Tooltip
          title={
            'Represents campaigns which are triggered by specific predefined events'
          }>
          <Typography>{row[tableTitle.id]}</Typography>
        </Tooltip>
      );
    } else if (row[tableTitle.id] === 'Manual') {
      return (
        <Tooltip title={'Represents campaigns which are triggered on demand'}>
          <Typography>{row[tableTitle.id]}</Typography>
        </Tooltip>
      );
    } else if (row[tableTitle.id] === undefined) {
      return (
        <Tooltip
          title={
            'Represents campaigns which are triggered by survey responses'
          }>
          <Typography>{'Survey Response'}</Typography>
        </Tooltip>
      );
    } else {
      return (
        <Tooltip
          title={
            'Represents campaigns which are scheduled to run at specific date and time'
          }>
          <Typography>{row[tableTitle.id]}</Typography>
        </Tooltip>
      );
    }
  };

  return (
    <Card className={clsx(classes.root, classes.rootContent)}>
      <CardContent className={classes.content}>
        <PerfectScrollbar>
          {isLoading && (
            <div className={classes.loader}>
              <CircularProgress />
            </div>
          )}
          {!isLoading && (
            <div className={classes.inner}>
              <Table>
                <TableHead>
                  <TableRow>
                    {tableTitles.map(tableTitle => (
                      <React.Fragment key={tableTitle.id + 'row'}>
                        {tableTitle.id === 'actionButton' ? (
                          <TableCell key={tableTitle.id} />
                        ) : (
                          <React.Fragment>
                            {disableTableSort || !tableTitle?.enableSort ? (
                              <TableCell key={tableTitle.id}>
                                {tableTitle.label}
                              </TableCell>
                            ) : (
                              <TableCell key={tableTitle.id}>
                                <TableSortLabel
                                  active={tableTitle.id === sortBy}
                                  direction={
                                    tableTitle.id === sortBy
                                      ? sortOrder.toLowerCase()
                                      : 'asc'
                                  }
                                  onClick={() =>
                                    onClickTableSort(tableTitle.id)
                                  }>
                                  {tableTitle.label}
                                </TableSortLabel>
                              </TableCell>
                            )}
                          </React.Fragment>
                        )}
                      </React.Fragment>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {totalCount > 0 &&
                    filteredRows.map(row => (
                      <React.Fragment key={row.id}>
                        <TableRow
                          onDoubleClick={() => onDoubleClick(row)}
                          className={typeof onDoubleClickRow === 'function' ? classes.tableRow : ''}
                          hover
                          key={row.id}>
                          {tableTitles.map(tableTitle => (
                            <React.Fragment key={row.id + tableTitle.id}>
                              {tableTitle.id === 'actionButton' ? (
                                <TableCell className={classes.rightCol}>
                                  <ActionButton
                                    options={actionButtonOptions(row)}
                                  />
                                </TableCell>
                              ) : (
                                <TableCell>
                                  {tableTitle.hasIcons && renderIcons(row)}
                                  {tableTitle.hasLink && row[tableTitle.id] ? (
                                    <Link className={classes.tableRow} onClick={() => onClickLink(tableTitle, row)}>
                                      {row[tableTitle.id]}
                                    </Link>
                                  ) : (
                                    tableTitle.id === 'sendDate'
                                      ? renderCampaignToolTip(row, tableTitle)
                                      : (tableTitle.enableTooltip && row[tableTitle.id]
                                        ? renderTooltip(tableTitle.tooltip ? row[tableTitle.tooltip] : row[tableTitle.id], row[tableTitle.id])
                                        : (row[tableTitle.id] ? row[tableTitle.id] : '-'))
                                  )}
                                  {tableTitle.hasGlobalDefault &&
                                    renderGlobalDefaultTags(row)}
                                  {tableTitle.hasTenantDefault &&
                                    renderTenantDefaultTags(row)}
                                  {tableTitle.hasTags && renderUserTags(row)}
                                  {tableTitle.hasCopy && renderCopyIcon(row)}
                                </TableCell>
                              )}
                            </React.Fragment>
                          ))}
                        </TableRow>
                      </React.Fragment>
                    ))}
                </TableBody>
              </Table>
            </div>
          )}
        </PerfectScrollbar>
        {!isLoading && totalCount === 0 && (
          <React.Fragment>
            <Typography className={classes.noResultsDiv}>
              No records found.
            </Typography>
            <Divider></Divider>
          </React.Fragment>
        )}
      </CardContent>
      <CardActions className={classes.actions}>
        <TablePagination
          component="div"
          count={totalCount}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          page={pageNo}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </CardActions>
    </Card>
  );
};

TableComponent.propTypes = {
  isLoading: PropTypes.bool,
  tableTitles: PropTypes.array,
  onDoubleClickRow: PropTypes.func,
  actionButtonOptions: PropTypes.func,
  filteredRows: PropTypes.array,
  action: PropTypes.object
};

export default TableComponent;
